import { useEffect, useRef, useState } from "react";
import { Menu } from "lucide-react";

import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";
import { deriveAnalyticsElementId } from "@/lib/analytics/deriveAnalyticsElementId";
import { useSheetAnalytics } from "@/lib/analytics/useSheetAnalytics";
import { Logo } from "@/shared/design-system/Logo";
import { Button } from "@/shared/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/shared/ui/sheet";

import {
  homeNavigationLink,
  primaryNavigationLinks,
  publicNavigationDesktopQuery,
  signInNavigationLink,
} from "@/lib/navigationConfig";
import { PublicNavigationLink } from "./PublicNavigationLink";
import { focusPublicMain } from "./focusPublicMain";

import "./mobile-navigation.css";

type MobileNavigationProps = {
  baseAnalyticsElementId: AnalyticsElementId;
  pathname: string;
};

/** The same destinations as the desktop header, with the shared sheet's keyboard and scroll handling. */
export function MobileNavigation({ baseAnalyticsElementId, pathname }: MobileNavigationProps) {
  const [open, setOpen] = useState(false);
  const destinationAfterClose = useRef<string | null>(null);
  const elementId = (suffix: string) => deriveAnalyticsElementId(baseAnalyticsElementId, suffix);
  useSheetAnalytics({ analyticsElementId: elementId("sheet"), open });

  useEffect(() => {
    const desktop = window.matchMedia(publicNavigationDesktopQuery);
    const closeOnDesktop = () => {
      if (desktop.matches) setOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    return () => desktop.removeEventListener("change", closeOnDesktop);
  }, []);

  const selectDestination = (href: string) => {
    destinationAfterClose.current = href;
    setOpen(false);
  };

  return (
    <div className="public-mobile-navigation">
      <Sheet
        open={open}
        onOpenChange={(nextOpen) => {
          destinationAfterClose.current = null;
          setOpen(nextOpen);
        }}
      >
        <SheetTrigger asChild>
          <Button
            analyticsElementId={elementId("open")}
            variant="ghost"
            size="icon"
            className="public-mobile-navigation-trigger"
            aria-label="Open navigation"
          >
            {/* A `size-*` class, not lucide's `size` prop: the button's
                `[&_svg:not([class*='size-'])]:size-4` rule keys off the class, so the prop alone
                was silently overridden and the only route into mobile navigation drew at 16px. */}
            <Menu className="size-5" strokeWidth={2.5} aria-hidden="true" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="public-mobile-navigation-sheet"
          data-public-navigation-menu
          closeAnalyticsElementId={elementId("close")}
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            // A section link should place the reader at that content after the focus trap
            // and scroll lock release; Escape and the close button return to the trigger.
            const destination = destinationAfterClose.current;
            destinationAfterClose.current = null;
            const sectionId = destination?.startsWith("#") ? destination.slice(1) : null;
            // Route navigation can unmount this header while the next page loads.
            // Never send focus back to the departing drawer's trigger.
            if (destination) event.preventDefault();
            const section = sectionId ? document.getElementById(sectionId) : null;
            if (section) {
              event.preventDefault();
              section.tabIndex = -1;
              section.focus({ preventScroll: true });
              section.scrollIntoView({ block: "start", behavior: "instant" });
            } else if (destination === pathname || window.matchMedia(publicNavigationDesktopQuery).matches) {
              // The current-page link or a hidden mobile trigger needs a visible destination.
              event.preventDefault();
              focusPublicMain();
            }
          }}
        >
          <SheetHeader>
            <SheetTitle>Explore Evangelist</SheetTitle>
          </SheetHeader>
          <nav aria-label="Mobile navigation" className="public-mobile-navigation-links">
            {primaryNavigationLinks.map((link) => (
              <PublicNavigationLink
                key={link.analyticsSuffix}
                link={link}
                pathname={pathname}
                analyticsElementId={elementId(link.analyticsSuffix)}
                onNavigate={selectDestination}
              />
            ))}
            <PublicNavigationLink
              analyticsElementId={elementId("sign-in")}
              link={signInNavigationLink}
              pathname={pathname}
              onNavigate={selectDestination}
              className="public-mobile-navigation-sign-in"
            />
          </nav>
        </SheetContent>
      </Sheet>
      <PublicNavigationLink analyticsElementId={elementId("home")} link={homeNavigationLink} pathname={pathname}>
        <Logo variant="symbol" className="public-mobile-navigation-logo" imageSizes="32px" />
      </PublicNavigationLink>
      <PublicNavigationLink
        analyticsElementId={elementId("header-sign-in")}
        link={signInNavigationLink}
        pathname={pathname}
        className="public-mobile-navigation-header-sign-in"
      >
        <span>Sign In</span>
      </PublicNavigationLink>
    </div>
  );
}
